import type { ModelRegistry } from './types';
import { structuralModels } from './structural';
import { decorativeModels } from './decorative';
import { decorativeFurnitureModels } from './decorativeFurniture';
import { naturalModels } from './natural';
import { collectibleModels } from './collectibles';
import { gameOfThronesModels } from './themed/gameOfThrones';
import { wonderlandModels } from './themed/wonderland';
import { nightmareChristmasModels } from './themed/nightmareChristmas';
import { strangerThingsModels } from './themed/strangerThings';
import { strangerThingsEnvironmentModels } from './themed/strangerThingsEnvironment';
import { jurassicParkModels } from './themed/jurassicPark';
import { lordOfTheRingsModels } from './themed/lordOfTheRings';
import { reveilEducatriceModels } from './themed/reveilEducatrice';
// Biome models
import { jungleModels } from './biomes/jungle';
import { desertModels } from './biomes/desert';
import { underwaterModels } from './biomes/underwater';
import { iceModels } from './biomes/ice';
import { swampModels } from './biomes/swamp';
import { mountainModels } from './biomes/mountain';
// NPC registry for fallback
import { npcModelRegistry } from '../npcs';

// Re-export types
export type { ModelProps, ModelRegistry } from './types';

// Combine all model registries
export const modelRegistry: ModelRegistry = {
  ...structuralModels,
  ...decorativeModels,
  ...decorativeFurnitureModels,
  ...naturalModels,
  ...collectibleModels,
  ...gameOfThronesModels,
  ...wonderlandModels,
  ...nightmareChristmasModels,
  ...strangerThingsModels,
  ...strangerThingsEnvironmentModels,
  ...jurassicParkModels,
  ...lordOfTheRingsModels,
  ...reveilEducatriceModels,
  // Biomes
  ...jungleModels,
  ...desertModels,
  ...underwaterModels,
  ...iceModels,
  ...swampModels,
  ...mountainModels,
};

// Helper to get a model component by ID
export function getModelComponent(modelId: string) {
  // First check in the model registry
  if (modelRegistry[modelId]) {
    return modelRegistry[modelId];
  }

  // If modelId starts with 'npc_', try to find in NPC registry
  if (modelId.startsWith('npc_')) {
    const npcType = modelId.replace('npc_', '');
    if (npcModelRegistry[npcType]) {
      return npcModelRegistry[npcType];
    }
  }

  return null;
}

// List of model IDs that should float
export const FLOATING_TYPES = ['crystal', 'orb', 'gem', 'spectral_key', 'spectral_remnant', 'ancient_seed'];

// List of model IDs that should rotate
export const ROTATING_TYPES = ['orb', 'key', 'spectral_key'];

// List of model IDs that should pulsate
export const PULSATING_TYPES = ['crystal', 'orb', 'spectral_remnant', 'magic_flower', 'spectral_key', 'ancient_seed'];

// List of model IDs that should sway (vegetation)
export const SWAYING_TYPES = [
  'tree', 'ancient_tree', 'bush', 'plant', 'flower',
  'palm_tree', 'banana_tree', 'giant_fern', 'tropical_flower',
  'jungle_vine', 'date_palm', 'pine_tree', 'snowy_pine',
  'seaweed', 'reed'
];

// List of object types that should NOT become transparent when occluding
export const NON_OCCLUDING_TYPES = [
  'spectral_remnant', 'sage', 'npc', 'orb', 'crystal',
  'spectral_key', 'magic_flower', 'stairs', 'platform',
  'ramp', 'bridge', 'spectral_bridge', 'ancient_seed'
];
